import type { Readable, Writable } from "svelte/store";
import { derived, get, writable } from "svelte/store";

export type Component = {
    component: any;
    props: Record<string, any>;
};

export type PanelId = string;
export type PanelLocation =
    | "left-upper"
    | "left-lower"
    | "right-upper"
    | "right-lower"
    | "bottom-left"
    | "bottom-right"
    | `sub-${string}`;

export type PanelData = {
    id: PanelId;
    name: string;
    icon: Component;
    initialLocation?: PanelLocation;
    initialActive?: boolean;
    activate?: () => void;

    templateOptions?: {
        enable?: boolean;
        scroll?: boolean;
        resize?: boolean;
        header?: Component | { enabled: false };
    };
} & Component;

export type EditorLayoutState = Writable<Record<string, any>>;
export type EditorPanels = Record<PanelId, PanelData>;

export type PanelBarOptions = {
    visible: boolean;
};
export type PanelOptions = {
    size?: string;
};
export type EditorLayoutOptions = {
    panelBars: {
        left: PanelBarOptions;
        right: PanelBarOptions;
    };
    panels: {
        left: PanelOptions;
        right: PanelOptions;
        bottom: PanelOptions;
    };
};

type SubLayoutBase = {
    id: string;
};
export type SplitLayout = SubLayoutBase & {
    type: "split";
    direction: "horizontal" | "vertical";
    initial: PanelId[];
    options: {
        minSize?: number;
    };
};
export type TabLayout = SubLayoutBase & {
    type: "tab";
    position: "top" | "bottom" | "left" | "right";
    initial: PanelId[];
    initialOpen: PanelId;
    closable?: boolean;
    options: {};
};

type Min = {
    min?: number;
};

type Initial = {
    initial?: number;
};

type Row<Id> = {
    areas: [Id, Id, Id];
};

type Areas<Id> = {
    rows: [Row<Id> & Min & Initial, Row<Id> & Min, Row<Id> & Min & Initial];
    columns: [Min & Initial, Min, Min & Initial];
};
export type GridLayout<
    AreaId extends string = string,
    FillId extends AreaId = AreaId,
    ResizeId extends AreaId = AreaId,
> = SubLayoutBase & {
    type: "grid";
    areas: Areas<AreaId>;
    fill: FillId;
    resize: ResizeId[];
};
export type SubLayout = SplitLayout | TabLayout | GridLayout;

export type SubLayoutState = Record<string, any> & {
    open: (item: string) => void;
    close: (item: string) => void;
    toggle: (item: string) => void;
};

export type EditorLayout = {
    panels: Readable<EditorPanels>;

    group: {
        leftUpper: Writable<PanelId[]>;
        leftLower: Writable<PanelId[]>;
        rightUpper: Writable<PanelId[]>;
        rightLower: Writable<PanelId[]>;
        bottomLeft: Writable<PanelId[]>;
        bottomRight: Writable<PanelId[]>;
    };

    subLayouts: Record<string, SubLayout & { state: Writable<SubLayoutState> }>;

    active: {
        leftUpper: Writable<PanelId | undefined>;
        leftLower: Writable<PanelId | undefined>;
        rightUpper: Writable<PanelId | undefined>;
        rightLower: Writable<PanelId | undefined>;
        bottomLeft: Writable<PanelId | undefined>;
        bottomRight: Writable<PanelId | undefined>;
    };

    state: EditorLayoutState & {
        create: <T>(key: string, value: T) => Writable<T>;
        createPanelState: <T>(id: PanelId, key: string, value: T) => Writable<T>;
    };

    open: (id: PanelId, location?: PanelLocation) => void;
    close: (id: PanelId, location?: PanelLocation) => void;
    toggle: (id: PanelId, location?: PanelLocation) => void;

    reset: () => void;

    options: EditorLayoutOptions;
};

type DeepPartial<K> = {
    [attr in keyof K]?: K[attr] extends object ? DeepPartial<K[attr]> : K[attr];
};
export type CreateLayoutOptions = {
    state: EditorLayoutState;
    options?: DeepPartial<EditorLayoutOptions>;
};

const defaultTemplateOptions = {
    enable: true,
    scroll: true,
};

export function createLayout(
    panels: PanelData[],
    subLayouts: SubLayout[],
    options: CreateLayoutOptions
): EditorLayout {
    const state = options.state;
    state.update(($s) => {
        if ($s) return $s;
        return {};
    });

    const opts = options.options ?? {};

    function create<T>(key: string, value: T) {
        state.update(($s) => ({ ...$s, [key]: $s[key] === undefined ? value : $s[key] }));
        return {
            ...derived(state, ($s) => $s[key]),
            set: (value: T) => {
                state.update(($s) => ({ ...$s, [key]: value }));
            },
            update: (fn: (value: T) => T) => {
                state.update(($s) => ({ ...$s, [key]: fn($s[key]) }));
            },
        };
    }

    function createPanelState<T>(id: PanelId, key: string, value: T) {
        return create(`panel-state-${id}-${key}`, value);
    }

    function addPanel(panel: PanelData) {
        if (get(panelStore)[panel.id]) throw new Error(`Panel '${panel.id}' already exists.`);

        panel.templateOptions = Object.assign({}, defaultTemplateOptions, panel.templateOptions);

        panelStore.update(($s) => ({ ...$s, [panel.id]: panel }));
    }

    const panelStore = writable({} as EditorPanels);
    panels.forEach(addPanel);

    const withInit = (loc: PanelLocation) =>
        Object.values(get(panelStore))
            .filter((panel) => panel.initialLocation === loc)
            .map((panel) => panel.id);

    const findActive = (loc: PanelLocation) =>
        Object.values(get(panelStore))
            .filter((panel) => panel.initialLocation === loc)
            .find((panel) => panel.initialActive)?.id;

    const layout = {
        panels: { subscribe: panelStore.subscribe },

        subLayouts: subLayouts.reduce(
            (acc, layout) => {
                acc[layout.id] = { ...layout, state: writable() };
                return acc;
            },
            {} as Record<string, SubLayout & { state: Writable<SubLayoutState> }>
        ),

        group: {
            leftUpper: create(`items-left-upper`, withInit("left-upper")),
            leftLower: create(`items-left-lower`, withInit("left-lower")),

            rightUpper: create(`items-right-upper`, withInit("right-upper")),
            rightLower: create(`items-right-lower`, withInit("right-lower")),

            bottomLeft: create(`items-bottom-left`, withInit("bottom-left")),
            bottomRight: create(`items-bottom-right`, withInit("bottom-right")),
        },

        active: {
            leftUpper: create(`active-left-upper`, findActive("left-upper")),
            leftLower: create(`active-left-lower`, findActive("left-lower")),
            rightUpper: create(`active-right-upper`, findActive("right-upper")),
            rightLower: create(`active-right-lower`, findActive("right-lower")),
            bottomLeft: create(`active-bottom-left`, findActive("bottom-left")),
            bottomRight: create(`active-bottom-right`, findActive("bottom-right")),
        },

        state: {
            ...state,
            create,
            createPanelState,
        },
        open: (id, location = undefined) => {
            const subLayout = layout.subLayouts[location!];
            if (subLayout) {
                // close all other
                get(subLayout.state).open(id);
                return;
            }

            if (get(layout.group.leftUpper).indexOf(id) !== -1) layout.active.leftUpper.set(id);
            else if (get(layout.group.leftLower).indexOf(id) !== -1)
                layout.active.leftLower.set(id);
            else if (get(layout.group.rightUpper).indexOf(id) !== -1)
                layout.active.rightUpper.set(id);
            else if (get(layout.group.rightLower).indexOf(id) !== -1)
                layout.active.rightLower.set(id);
            else if (get(layout.group.bottomLeft).indexOf(id) !== -1)
                layout.active.bottomLeft.set(id);
            else if (get(layout.group.bottomRight).indexOf(id) !== -1)
                layout.active.bottomRight.set(id);
            else {
                const panel = get(panelStore)[id];
                if (!panel) throw new Error(`Panel '${id}' does not exist.`);
                const loc = location ?? panel.initialLocation ?? "left-upper";

                if (loc === "left-upper") layout.active.leftUpper.set(id);
                else if (loc === "left-lower") layout.active.leftLower.set(id);
                else if (loc === "right-upper") layout.active.rightUpper.set(id);
                else if (loc === "right-lower") layout.active.rightLower.set(id);
                else if (loc === "bottom-left") layout.active.bottomLeft.set(id);
                else if (loc === "bottom-right") layout.active.bottomRight.set(id);
                else throw new Error(`Invalid location '${loc}' for panel '${id}'.`);
            }
        },
        close: (id, location) => {
            const subLayout = layout.subLayouts[location!];
            if (subLayout) {
                get(subLayout.state).close(id);
                return;
            }
        },
        toggle: (id, location) => {
            const subLayout = layout.subLayouts[location!];
            if (subLayout) {
                // close all other
                get(subLayout.state).toggle(id);
                return;
            }
        },
        reset: () => {
            layout.group.leftUpper.set(withInit("left-upper"));
            layout.group.leftLower.set(withInit("left-lower"));
            layout.group.rightUpper.set(withInit("right-upper"));
            layout.group.rightLower.set(withInit("right-lower"));
            layout.group.bottomLeft.set(withInit("bottom-left"));
            layout.group.bottomRight.set(withInit("bottom-right"));

            layout.active.leftUpper.set(findActive("left-upper"));
            layout.active.leftLower.set(findActive("left-lower"));
            layout.active.rightUpper.set(findActive("right-upper"));
            layout.active.rightLower.set(findActive("right-lower"));
            layout.active.bottomLeft.set(findActive("bottom-left"));
            layout.active.bottomRight.set(findActive("bottom-right"));
        },

        options: {
            panelBars: {
                left: {
                    visible: opts.panelBars?.left?.visible ?? true,
                },
                right: {
                    visible: opts.panelBars?.right?.visible ?? true,
                },
            },
            panels: {
                left: {
                    size: opts.panels?.left?.size ?? undefined,
                },
                right: {
                    size: opts.panels?.right?.size ?? undefined,
                },
                bottom: {
                    size: opts.panels?.bottom?.size ?? undefined,
                },
            },
        },
    } satisfies EditorLayout;

    return layout;
}
